import {ChangeDetectorRef, Component, TemplateRef, ViewChild} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ActivatedRoute, Router} from "@angular/router";
import {DirectoryService} from "../../shared/directory.service";
import {DirectoryDescription} from "../../interfaces/directoryDescription";
import {Directory, DirectoryStatus} from "../../interfaces/directory";
import {Paged} from "../../interfaces/paged";
import {NbDialogService} from "@nebular/theme";
import {EditDialogComponent, EditType} from "../../dialogs/edit-dialog/edit-dialog.component";
import {TranslateService} from "@ngx-translate/core";
import {Globals} from "../../shared/Globals";
import {activeFilterKeys, DualAxisDelegate, DualAxisEvent} from "../../dual-axis/dual-axis.component";
import {TreeNode} from "../../interfaces/treenode";
import {ColDef, ColType} from "../../interfaces/colDef";
import {TableOptions} from "../../settings/table-options/table-options.component";
import {Subject} from "rxjs";
import {SettingsService} from "../../shared/settings.service";
import {NotifierService} from "../../shared/notifier.service";
import {TeamConfigDrawerComponent} from "../team-config-drawer/team-config-drawer.component";
import {Department} from "../../interfaces/department";

@Component({
    selector: 'bst-directory',
    templateUrl: './directory.component.html',
    styleUrls: ['../../project/projects.component.scss', './directory.component.scss'],
    standalone: false
})
export class DirectoryComponent extends SubscribedComponent implements DualAxisDelegate<Directory>{

  public name: string;
  public description: DirectoryDescription;
  public status: DirectoryStatus = DirectoryStatus.ACTIVE;
  public DirectoryStatus = DirectoryStatus;
  public Globals = Globals;
  public DualAxisEvent = DualAxisEvent;
  public count: number = null;

  data: TreeNode<Directory>[] = [];
  @ViewChild('cellTemplate', {static: true, read: TemplateRef}) cellTemplateOutlet: TemplateRef<any>;

  events = new Subject<DualAxisEvent>();
  stickyColumnsRight: ColDef[] = [];
  stickyColumnsLeft: ColDef[] = [];
  standardColumns: ColDef[] = [];
  otherColumns: ColDef[] = [];
  editable = null;
  options: TableOptions = {
    display: [],
    others: {},
    filter: {},
    sorts: []
  };

  public get filterCount(): number { return activeFilterKeys(this.options?.filter).length; }

  // Sorter is only used when canSort=true (drag-drop mode)
  // When canSort=false, sorting is handled by the backend
  sorter: ((a: TreeNode<Directory>, b:TreeNode<Directory>) => number) | null = null;

  private sortindexSorter = (a: TreeNode<Directory>, b:TreeNode<Directory>) => {
    if(a.data.sortindex && b.data.sortindex){
      return a.data.sortindex - b.data.sortindex
    }
    if(a.data.sortindex){
      return -1;
    }
    if(b.data.sortindex){
      return 1;
    }
    return 0;
  }

  constructor(protected route: ActivatedRoute,
              protected directoryService: DirectoryService,
              protected dialogService: NbDialogService,
              protected detector: ChangeDetectorRef,
              protected router: Router,
              protected notifier: NotifierService,
              protected settings: SettingsService,
              protected translate: TranslateService) {
    super();
  }

  get namekey(): string{
    return 'name';
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subs.push(this.route.paramMap.subscribe(async (routeMap) => {
      const name = routeMap.get('name');
      if(!this.loading.getValue() && name !== this.name) {
        this.name = name;
        await this.describe(name);
      }
    }));
  }

  async paged(page: number = 1): Promise<Paged<Directory>>{

    this.options.filter.status = this.options.others.status ?? DirectoryStatus.ACTIVE;

    // map sorting
    // const _sorts:{col: string, dir: number}[] = [];
    if(this.description && this.description.canSort){
      this.options.sorts = [{col: 'sortindex', dir: 1}];
    }
    // else {
    //   for (let s of this.sorts) {
    //     const col = this.standardColumns.find(si => si.name === s.col);
    //     if (col) {
    //       if (col.sort instanceof Array) {
    //         for (let so of col.sort) {
    //           _sorts.push({col: so, dir: s.dir});
    //         }
    //       } else if (col.name) {
    //         _sorts.push({col: col.name, dir: s.dir});
    //       }
    //     }
    //   }
    // }

    this.loading.next(true);
    const directory = await this.directoryService.find(this.name, page, this.options.filter, this.options.sorts);
    this.loading.next(false);

    if(page <= 1){
      this.data = [];
    }
    this.refreshEditable();

    if(directory && directory.data){
      this.count = directory.total;
      this.data = this.data.concat(directory.data.map((o: Directory) => {
        return this.toNode(o);
      }));
      console.log(this.data);
      return directory;
    }
    this.count = null;
    return null;
  }

  protected toNode(o: Directory): TreeNode<Directory>{
    return {
      data: o,
      expanded: false,
      children: []
    }
  }

  protected async describe(directory: string){
    this.loading.next(true);
    this.data = [];
    this.stickyColumnsLeft = [];
    this.stickyColumnsRight = [];
    this.standardColumns = [];
    this.description = null;
    this.count = null;

    // Toasted by errorify; keep loading unwedged or the paramMap guard ignores every later route.
    const describe: DirectoryDescription = await this.directoryService.describeDirectory(directory).catch(() => null);
    if(describe){
      this.standardColumns = describe.colDefs;
      this.options = {
        filter: {},
        others: {
          status: DirectoryStatus.ACTIVE
        },
        display: this.settings.mergeTableColumns('table_dir_' + this.name, this.standardColumns),
        sorts: []
      };

      // Default sort
      if(describe.canSort){
        this.options.sorts = [];
        // Enable client-side sortindex sorting for drag-drop mode
        this.sorter = this.sortindexSorter;
      }
      else if(this.standardColumns.length) {
        this.options.sorts = [{col: this.standardColumns[0].name, dir: 1}];
        // Disable client-side sorting - let backend handle it
        this.sorter = null;
      }

      this.description = describe;
    }
    this.loading.next(false);
    return this.description;
  }

  public edit(row: TreeNode<Directory>) {
    this.subs.push(this.dialogService.open<EditDialogComponent<Directory>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        title: this.description.title,
        subtitle: row.data.name,
        description: this.description.colDefs,
        data: row.data
      }
    }).onClose.subscribe(async choice => {
      if(choice){
        const newitem = await this.directoryService.saveDirectory(this.name, row.data.id, <Directory>choice, this.translate.instant('itemsaved', {value: choice[this.namekey]}));
        this.updateitem(row, newitem);
      }
    }));
  }

  public manageTeam(row: TreeNode<Directory>) {
    this.dialogService.open<TeamConfigDrawerComponent>(TeamConfigDrawerComponent, {
      dialogClass: 'drawer-right',
      context: {
        deptId: row.data.id,
        deptName: row.data[this.namekey],
        parentId: (row.data as Department).parent_id ?? null,
      },
    });
  }

  protected refreshEditable(){
    this.editable = {};
    if(this.description && this.description.canEdit){
      this.editable.edit = this.edit;
    }
    if(this.description && this.description.canArchive){
      this.editable.archive = this.archive;
    }
    if(this.description && this.description.canSort){
      this.editable.sort = this.sort;
    }
    if(this.name === 'departments'){
      this.editable.manageTeam = this.manageTeam;
    }
  }

  public async archive(row: TreeNode<Directory>) {
    if(await this.notifier.confirm('archivecheck', row.data.name)){
      const newitem = await this.directoryService.changeDirectoryStatus(this.name, row.data.id, DirectoryStatus.ARCHIVED);
      if(newitem) {
        this.data = this.data.filter(o => o !== row);
        this.count -= 1;
      }
    }
  }

  public async unarchive(row: TreeNode<Directory>) {
    const newitem = await this.directoryService.changeDirectoryStatus(this.name, row.data.id, DirectoryStatus.ACTIVE);
    if(newitem){
      this.data = this.data.filter(o => o !== row);
      this.count -= 1;
    }
  }

  public async delete(row: TreeNode<Directory>){
    if(await this.notifier.confirm('deletecheck', row.data.name, 'deleteforever', 'danger')){
      try {
        await this.directoryService.deleteDirectory(this.name, row.data.id);
        this.data = this.data.filter(o => o !== row);
        this.count -= 1;
      } catch(error){
        console.error(error);
      }
    }
  }

  public async sort(dragging: TreeNode<Directory>, from, to){
    if(from && to && from != to) {
      this.loading.next(true);
      const updates = await this.directoryService.changeDirectorySort(this.name, from, to, dragging.data[this.namekey] + ' Moved!');
      updates.forEach((o: Directory) => {
        const index = this.data.findIndex((o1: TreeNode<Directory>) => o1.data.id === o.id);
        if(index > -1) {
          this.data[index] = this.toNode(o);
        }
      });

      this.loading.next(false);
    }
  }

  private updateitem(row: TreeNode<Directory>, newitem: Directory){
    const index = this.data.indexOf(row);
    this.data[index] = this.toNode(newitem);
  }

  public newDirectoryItem() {
    this.subs.push(this.dialogService.open<EditDialogComponent<Directory>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        title: this.description.title,
        description: this.description.colDefs,
        type: EditType.ADD,
        data: null
      }
    }).onClose.subscribe(async choice => {
      if(choice){
        const newitem = await this.directoryService.addDirectory(this.name, <Directory>choice, this.translate.instant('itemadded', {value: choice[this.namekey]}));
        this.data.unshift(this.toNode(newitem));
        this.count = this.count ? this.count+1 : 1;
      }
    }));
  }

  /**
   * Get display value for select columns by looking up the name in the list
   */
  public getSelectDisplayValue(row: Directory, column: ColDef): string {
    const value = row[column.name];
    if (value == null || !column.list) {
      return null;
    }
    const item = column.list.find((i: any) => i.id === value);
    return item ? item.name : value;
  }

  get headerTitle(): string | { title: string; translate: boolean | Object } {
    return 'Directories';
  }
}
